import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, BookOpen, LayoutGrid } from "lucide-react";
import { sutras } from "@/data/sutras";
import { AnimatedWords } from "./HeroSection";
import { useCopy } from "@/lib/copyVariant";
import { img } from "@/lib/img";

export default function SutrasSection() {
  const [idx, setIdx] = useState(0);
  const [view, setView] = useState("card");
  const { c } = useCopy();
  const contextNote = c("sutrasContextNote");
  const next = () => setIdx((p) => (p + 1) % sutras.length);
  const prev = () => setIdx((p) => (p - 1 + sutras.length) % sutras.length);
  const s = sutras[idx];

  return (
    <section id="sutras" className="py-16 sm:py-24 md:py-36 px-5 md:px-10 relative overflow-hidden" data-testid="sutras-section"
      style={{ background: "linear-gradient(180deg, #fff8f3 0%, #f3ece4 50%, #fff8f3 100%)" }}>
      {/* Sage pattern overlay */}
      <img src={img("https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/ytpos1qk_3.png", { w: 1400 })}
        alt="" aria-hidden="true" loading="lazy" decoding="async" width="1400" height="900" className="absolute inset-0 w-full h-full object-cover opacity-[0.06] pointer-events-none" />
      {/* Decorative icons */}
      <img src={img("https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/kvoym993_65cc832cacc7ac557a389a4c_orb.avif", { w: 160 })}
        alt="" aria-hidden="true" loading="lazy" decoding="async" width="80" height="80" className="absolute top-16 right-8 md:right-20 w-12 md:w-20 opacity-50 pointer-events-none animate-float" />
      <img src={img("https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/vn212xvd_65cc80a24a43de1ff7dff00e_banner%20img%20p.avif", { w: 500 })}
        alt="" aria-hidden="true" loading="lazy" decoding="async" width="256" height="160" className="absolute bottom-8 left-0 w-40 md:w-64 opacity-30 pointer-events-none rounded-2xl" />
      <div className="max-w-[1400px] mx-auto relative">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 sm:mb-16 gap-4">
          <div>
            <motion.span className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#96aeb4] font-semibold block mb-4 sm:mb-6"
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>{c("sutrasEyebrow")}</motion.span>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#464646] leading-[1.12] tracking-tight">
              <AnimatedWords text={c("sutrasHeadingL1")} />
              <br className="hidden sm:block" />
              <span className="text-gradient-sage"><AnimatedWords text={c("sutrasHeadingL2")} delay={0.25} /></span>
            </h2>
            {contextNote && (
              <motion.p
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: 0.4, duration: 0.6 }}
                className="mt-4 sm:mt-5 text-sm sm:text-base text-[#464646]/70 leading-relaxed max-w-xl"
                data-testid="sutras-context-note"
              >
                {contextNote}
              </motion.p>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setView("card")} data-testid="view-card-btn"
              className={`p-2.5 rounded-full transition-all ${view === "card" ? "bg-[#464646] text-white" : "border border-[#464646]/15 text-[#464646]/60 hover:text-[#464646] bg-white"}`}>
              <BookOpen size={15} /></button>
            <button onClick={() => setView("grid")} data-testid="view-grid-btn"
              className={`p-2.5 rounded-full transition-all ${view === "grid" ? "bg-[#464646] text-white" : "border border-[#464646]/15 text-[#464646]/60 hover:text-[#464646] bg-white"}`}>
              <LayoutGrid size={15} /></button>
          </div>
        </div>

        {view === "card" ? (
          <div className="flex items-stretch gap-2 sm:gap-4 md:gap-6">
            <button onClick={prev} data-testid="sutra-prev-btn"
              className="border border-[#464646]/15 bg-white text-[#464646]/60 hover:text-[#464646] hover:border-[#464646]/40 shrink-0 rounded-full h-10 w-10 sm:h-12 sm:w-12 flex items-center justify-center transition-all self-center">
              <ChevronLeft size={18} /></button>
            <div className="flex-1 min-h-[180px]">
              <AnimatePresence mode="wait">
                <motion.div key={idx}
                  initial={{ opacity: 0, x: 50, scale: 0.97 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: -50, scale: 0.97 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-12" data-testid="sutra-card">
                  <div className="flex items-start gap-3 sm:gap-5 md:gap-8 mb-4 sm:mb-6">
                    <span className="text-4xl sm:text-6xl md:text-8xl font-accent font-light text-[#96aeb4]/25 leading-none select-none">
                      {String(s.id).padStart(2, "0")}</span>
                    <div className="pt-0.5 sm:pt-2">
                      <h3 className="font-serif text-lg sm:text-2xl md:text-3xl text-[#464646] font-medium leading-snug" data-testid="sutra-title">{s.title}</h3>
                      <p className="text-[9px] sm:text-[11px] text-[#96aeb4] font-semibold uppercase tracking-[0.3em] mt-1 sm:mt-2">Calmasutras {s.id}</p>
                    </div>
                  </div>
                  <p className="text-sm sm:text-base md:text-lg text-[#464646]/70 leading-relaxed" data-testid="sutra-description">{s.description}</p>
                </motion.div>
              </AnimatePresence>
            </div>
            <button onClick={next} data-testid="sutra-next-btn"
              className="border border-[#464646]/15 bg-white text-[#464646]/60 hover:text-[#464646] hover:border-[#464646]/40 shrink-0 rounded-full h-10 w-10 sm:h-12 sm:w-12 flex items-center justify-center transition-all self-center">
              <ChevronRight size={18} /></button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3" data-testid="sutras-grid">
            {sutras.map((su, i) => (
              <motion.button key={su.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.008, duration: 0.25 }}
                onClick={() => { setIdx(i); setView("card"); }}
                className="glass rounded-xl p-3 sm:p-4 text-left hover:bg-white hover:border-[#96aeb4]/40 hover:shadow-md transition-all cursor-pointer group"
                data-testid={`sutra-grid-${su.id}`}>
                <span className="text-base sm:text-lg font-serif text-[#96aeb4]/40 group-hover:text-[#96aeb4]/70 transition-colors">
                  {String(su.id).padStart(2, "0")}</span>
                <h4 className="text-[#464646]/70 group-hover:text-[#464646] font-medium mt-1 text-[10px] sm:text-xs leading-tight transition-colors">{su.title}</h4>
              </motion.button>
            ))}
          </div>
        )}
        <div className="mt-8 sm:mt-10 flex items-center justify-center gap-3" data-testid="sutra-progress">
          <span className="text-[#464646]/50 text-xs sm:text-sm">{idx + 1}/{sutras.length}</span>
          <div className="w-32 sm:w-48 h-[2px] bg-[#464646]/10 rounded-full overflow-hidden">
            <motion.div className="h-full bg-[#96aeb4] rounded-full" animate={{ width: `${((idx + 1) / sutras.length) * 100}%` }} transition={{ duration: 0.3 }} />
          </div>
        </div>
      </div>
    </section>
  );
}
